import { useRef, useEffect, useState } from 'react';
import gsap from 'gsap/all';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Image } from '@weareconceptstudio/core';

//* Styles
import HeroStyle from './style';

//* Lottie Files
import benefitHero from '@/lottie/benefitHero.json';

//* Component
import Editor from '@/components/Editor';
import TitleAnim from '@/modules/_animation/Title';
import StyledQuotedText from '@/components/StyledQuotedText';
import LottieAnimation from '@/components/LottieAnimation';

gsap.registerPlugin(ScrollTrigger);

const Hero = ({ title, description, images }) => {
	//! Refs
	const leftImageRef = useRef(null);
	const rightImageRef = useRef(null);
	const centerImageRef = useRef(null);
	const scrollTriggerRef = useRef(null);
	const titleRef = useRef(null);
	const descriptionRef = useRef();
	const reverseAnim = useRef(true);
	const reverseTrigger = useRef();

	//! State
	const [loadImage, setLoadImage] = useState(false);

	function renderImagesBySide(images, side) {
		return images
			.filter((item) => item.additional_info.img_style === side)
			.map((item) => (
				<Image
					onLoad={setLoadImage}
					key={item.image.src}
					src={item.image.src}
					alt={item.image.alt}
				/>
			));
	}

	useEffect(() => {
		if (leftImageRef.current && loadImage) {
			gsap.fromTo(descriptionRef.current, { opacity: 0 }, { opacity: 1, duration: 0.6, ease: 'power2.in', delay: 0.3 });

			scrollTriggerRef.current = ScrollTrigger.create({
				trigger: rightImageRef.current,
				start: 'top-=100% 75%',
				end: 'top-=100% top',
				// markers: true,
				onEnter: () => {
					if (reverseAnim.current) {
						reverseAnim.current = false;
						gsap.fromTo(leftImageRef.current, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'power2.in', delay: 0.15 });
						gsap.fromTo(
							leftImageRef.current,
							{
								y: '100%',
								rotate: -15,
							},
							{
								rotate: 0,
								y: '0%',
								duration: 1,
								delay: 0.1,
								ease: 'power2.out',
							}
						);

						gsap.fromTo([rightImageRef.current, centerImageRef.current], { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'power2.in', delay: 0.2, stagger: 0.3 });

						gsap.fromTo(
							[rightImageRef.current],
							{
								y: '100%',
								rotate: 15,
							},
							{
								rotate: 0,
								y: '0%',
								duration: 1,
								delay: 0.25,
								ease: 'power2.out',
							}
						);
						gsap.fromTo(
							[centerImageRef.current],
							{
								y: '100%',
								rotate: 15,
							},
							{
								rotate: 0,
								y: '0%',
								duration: 1,
								delay: 0.3,
								ease: 'power2.out',
							}
						);
					}
				},
			});
			reverseTrigger.current = ScrollTrigger.create({
				trigger: rightImageRef.current,
				start: 'top-=100% bottom',
				end: 'top-=100% top',
				// markers: true,
				onLeaveBack: () => {
					reverseAnim.current = true;

					gsap.set(leftImageRef.current, {
						opacity: 0,
						y: '100%',
						rotate: -15,
					});

					gsap.set([centerImageRef.current], {
						opacity: 0,
						y: '100%',
						rotate: 15,
					});
					gsap.set([rightImageRef.current], {
						opacity: 0,
						y: '100%',
						rotate: 15,
					});
					gsap.set(descriptionRef.current, {
						opacity: 0,
					});
				},
			});
		}

		return () => {
			if (scrollTriggerRef.current) {
				scrollTriggerRef.current.kill();
			}
			if (reverseTrigger.current) {
				reverseTrigger.current.kill();
			}
		};
	}, [loadImage]);

	return (
		<HeroStyle>
			<div className='outside-wrapper'>
				<div className='left-wrap'>
					<div
						ref={leftImageRef}
						className='wrap-outside-image opacity-zero'
					>
						{renderImagesBySide(images, 'left')}
					</div>
				</div>

				<div className='info-wrap'>
					<div className='hero-svg-block'>
						<LottieAnimation
							autoplay={false}
							isPlay={loadImage}
							isReverse={false}
							loop={false}
							animData={benefitHero}
						/>
					</div>

					<div
						ref={titleRef}
						className='title-wrap'
					>
						<TitleAnim load={loadImage}>
							<StyledQuotedText text={title} />
						</TitleAnim>
					</div>

					<div
						ref={descriptionRef}
						className='description-wrap opacity-zero'
					>
						<Editor
							data={description}
							className={'align-center'}
						/>
					</div>
				</div>

				<div className='right-wrap'>
					<div
						ref={rightImageRef}
						className='wrap-outside-image opacity-zero'
					>
						{renderImagesBySide(images, 'right')}
					</div>
				</div>

				<div className='center-wrap'>
					<div
						ref={centerImageRef}
						className='wrap-outside-image opacity-zero'
					>
						{renderImagesBySide(images, 'center')}
					</div>
				</div>
			</div>
		</HeroStyle>
	);
};

export default Hero;
